import * as React from 'react';
import { doesContainFiles } from "../services/utils";
import { Link } from 'react-router-dom';
import { Table, Tag, Button, Tooltip } from 'antd';
import { EditOutlined, PictureOutlined, EyeOutlined } from '@ant-design/icons';
import { openInEditor, VariantSelectorModal } from './openInEditor';

const COLUMNS = (onOpenVariantSelector) => [
    {
        title: 'Name',
        dataIndex: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        defaultSortOrder: 'ascend',
    },
    {
        title: 'Variants',
        dataIndex: 'catalogCount',
        align: 'center',
        width: 100,
        sorter: (a, b) => a.catalogCount - b.catalogCount,
        render: (catalogCount, record) => {
            const total = record.variantCount;
            if (total === 0) return <Tag>—</Tag>;
            const color = catalogCount === 0 ? 'error' : catalogCount < total ? 'warning' : 'success';
            const label = catalogCount === 0
                ? `None of the ${total} variants are visible in the catalog`
                : catalogCount === total
                    ? `All ${total} variant${total !== 1 ? 's' : ''} are visible in the catalog`
                    : `${catalogCount} of ${total} variants are visible in the catalog`;
            return (
                <Tooltip title={label}>
                    <Tag color={color}>{catalogCount} / {total}</Tag>
                </Tooltip>
            );
        },
    },
    {
        title: 'Creation Date',
        dataIndex: 'creationDate',
        sorter: (a, b) => new Date(a.creationDateRaw) - new Date(b.creationDateRaw),
        width: 180,
    },
    {
        title: 'Last Updated',
        dataIndex: 'updatedOn',
        sorter: (a, b) => new Date(a.updatedOnRaw) - new Date(b.updatedOnRaw),
        width: 180,
    },
    {
        title: 'Assets',
        dataIndex: 'isReady',
        align: 'center',
        width: 90,
        render: (isReady) => isReady
            ? <Tag color="success">Live</Tag>
            : <Tag color="error">Not Ready</Tag>,
    },
    {
        title: 'Metadata',
        align: 'center',
        width: 80,
        render: (_, record) => (
            <Tooltip title="Update metadata">
                <Link to={`/catalog/update/${record.id}`}>
                    <Button type="text" size="small" icon={<EditOutlined />} />
                </Link>
            </Tooltip>
        ),
    },
    {
        title: 'Assets',
        align: 'center',
        width: 70,
        render: (_, record) => doesContainFiles(record.type) ? (
            <Tooltip title="Update assets">
                <Link to={`/assets/${record.id}`}>
                    <Button type="text" size="small" icon={<PictureOutlined />} />
                </Link>
            </Tooltip>
        ) : <span style={{ opacity: 0.25 }}>—</span>,
    },
    {
        title: 'Editor',
        align: 'center',
        width: 70,
        render: (_, record) => record.variantCount === 0 ? (
            <span style={{ opacity: 0.25 }}>—</span>
        ) : (
            <Tooltip title="Open in editor">
                <Button
                    type="text"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => onOpenVariantSelector(record)}
                />
            </Tooltip>
        ),
    },
];

export class CatalogItemList extends React.Component {
    state = {
        selectorRecord: null,
    };

    openVariantSelector = (record) => {
        if (record.variants.length === 1) {
            openInEditor(record.id, record.variants[0].id);
        } else {
            this.setState({ selectorRecord: record });
        }
    };

    closeVariantSelector = () => {
        this.setState({ selectorRecord: null });
    };

    render() {
        const { selectorRecord } = this.state;

        const dataSource = [...this.props.items]
            .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
            .map(item => ({
                key: item.id,
                id: item.id,
                name: item.name,
                type: item.type,
                variants: item.variants,
                variantCount: item.variants.length,
                catalogCount: item.variants.filter(v => v.availableInCatalog).length,
                creationDate: new Date(item.creationDate).toLocaleString(),
                creationDateRaw: item.creationDate,
                updatedOn: new Date(item.updatedOn).toLocaleString(),
                updatedOnRaw: item.updatedOn,
                isReady: item.isReady,
            }));

        return (
            <>
                <Table
                    dataSource={dataSource}
                    columns={COLUMNS(this.openVariantSelector)}
                    pagination={false}
                    size="small"
                    showSorterTooltip={false}
                />

                <VariantSelectorModal
                    open={!!selectorRecord}
                    itemId={selectorRecord?.id}
                    title={selectorRecord?.name}
                    variants={(selectorRecord?.variants || []).map((v, i) => ({
                        id: v.id,
                        label: v.name || `Variant ${i + 1}`,
                    }))}
                    onClose={this.closeVariantSelector}
                />
            </>
        );
    }
}
